import { useState } from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import H4 from '@base/H4/H4';
import P2 from '@base/P2/P2';
import Paper from '@base/Paper/Paper';
import { breakpoints, Fonts, Spacings } from '@styles/index';
import useProfileStats from '@api/hooks/profile/useProfileStats';
import { getIsSubscribedToOffline } from '@selectors/subscription';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import Dynamic from '@base/Dynamic/Dynamic';
import { useSelector } from 'react-redux';
import { nFormatter } from '@utils/textHelper';

const LearnerFollowing = Dynamic(() => import('./LearnerFollowing'));

const Stats = styled(Paper)`
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  display: flex;
  align-items: flex-end;
  margin-right: ${Spacings.SPACING_4B};
  cursor: ${({ onClick }) => (onClick ? 'pointer' : 'default')};
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-right: 0;
    grid-column: span 3;
    flex-direction: column;
    align-items: flex-start;
    padding: ${Spacings.SPACING_3B};
  }
`;

const Value = styled(H4)`
  line-height: ${Fonts.PARA_LINE_HEIGHT};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    font-size: 18px;
  }
`;

const Label = styled(P2)`
  margin-left: ${Spacings.SPACING_2B};
  color: var(--color-text-primary);
  @media only screen and (max-width: ${breakpoints.mobile}) {
    margin-left: 0;
    margin-top: 4px;
    color: var(--color-text-secondary);
  }
`;

const EducatorStats = ({
  username,
  isEducator,
  isExEducator,
  onClickFollow
}) => {
  const [showLearnerFollowing, setShowLearnerFollowing] = useState(false);

  const selectedGoalUID = useSelectedGoalUID();

  const isOfflineSubscription = useSelector(
    getIsSubscribedToOffline(selectedGoalUID)
  );
  const { profileStats: stats = {} } = useProfileStats(username);
  const totalWatchTime = Math.round(
    stats.statistics?.totalWatchTimeMinutes / 60
  );
  const watchTime30Days = Math.round(stats.statistics?.lastMonthWatchTime / 60);
  const totalWatchMins = Math.round(stats.statistics?.totalWatchTime / 60);

  const onClickLearnerFollowing = () => setShowLearnerFollowing(true);
  const onCloseLearnerFollowing = () => setShowLearnerFollowing(false);

  const educatorItemsToShow = [
    {
      label: 'Watch mins',
      value: nFormatter(totalWatchTime) || '-'
    },
    {
      label: 'Watch mins (last 30 days)',
      value: nFormatter(watchTime30Days) || '-'
    },
    {
      label: 'Followers',
      value: nFormatter(stats.followers) || '-'
    }
  ];

  const exEducatorItemsToShow = [
    {
      label: 'Watch mins',
      value: nFormatter(totalWatchTime) || '-'
    }
  ];

  const learnerItemsToShow = [
    {
      label: 'Watch mins',
      value: nFormatter(totalWatchMins) || '-'
    },
    {
      label: 'Following',
      value: nFormatter(stats.followsCount) || '-',
      onClick: onClickLearnerFollowing
    }
  ];

  // eslint-disable-next-line no-nested-ternary
  const items = isEducator
    ? educatorItemsToShow
    : isExEducator
    ? exEducatorItemsToShow
    : learnerItemsToShow;
  return (
    <>
      {items.map(
        (item, index) =>
          (!isOfflineSubscription ||
            item.label.toLowerCase() === 'following') && (
            <Stats
              key={index.toString()}
              bgColor="var(--color-base-1)"
              onClick={item.onClick}
            >
              <Value>{item.value}</Value>
              <Label>{item.label}</Label>
            </Stats>
          )
      )}
      <LearnerFollowing
        showLearnerFollowing={showLearnerFollowing}
        learnerUsername={username}
        onCloseLearnerFollowing={onCloseLearnerFollowing}
        onClickFollow={onClickFollow}
      />
    </>
  );
};
export default EducatorStats;

EducatorStats.propTypes = {
  username: PropTypes.string.isRequired,
  isEducator: PropTypes.bool.isRequired,
  isExEducator: PropTypes.bool.isRequired,
  onClickFollow: PropTypes.func.isRequired
};
